/**
 * Format a number with commas (or spaces) as thousand separators.
 * This function is stable across server/client environments and won't cause hydration mismatches.
 *
 * @example
 * formatNumber(1234567)     => "1,234,567"
 * formatNumber(1234.56, 2)  => "1,234.56"
 * formatNumber(1000000, 0)  => "1,000,000"
 * formatNumber(1234.567, 2, ',', true) => "1,234.56" (floored)
 */
export function formatNumber(
  value: number,
  decimals: number = 0,
  separator: string = ',',
  floor: boolean = false,
): string {
  // Handle edge cases
  if (!isFinite(value)) return '0';

  // Round or floor to specified decimals
  const factor = Math.pow(10, decimals);
  const rounded = floor ? Math.floor(value * factor) / factor : Math.round(value * factor) / factor;

  // Split into integer and decimal parts
  const [integerPart, decimalPart] = rounded.toFixed(decimals).split('.');

  // Add thousand separators to integer part
  const formattedInteger = integerPart!.replace(/\B(?=(\d{3})+(?!\d))/g, separator);

  // Return with decimal part if needed
  return decimals > 0 && decimalPart ? `${formattedInteger}.${decimalPart}` : formattedInteger;
}

/**
 * Format a quantity number, showing decimal part only if it's not .00
 * This is useful for displaying quantities where we want to hide unnecessary decimals.
 *
 * @example
 * formatQuantity(100)      => "100"
 * formatQuantity(100.00)   => "100"
 * formatQuantity(100.50)   => "100.50"
 * formatQuantity(100.05)   => "100.05"
 * formatQuantity(1234.56)  => "1,234.56"
 */
export function formatQuantity(value: number, separator: string = ','): string {
  // Handle edge cases
  if (!isFinite(value)) return '0';

  // Check if number has a non-zero decimal part
  const hasDecimal = value % 1 !== 0;

  if (hasDecimal) {
    // Show with 2 decimal places if there's a decimal part
    return formatNumber(value, 2, separator);
  } else {
    // Show without decimal places if it's a whole number
    return formatNumber(value, 0, separator);
  }
}
